import {Box, Button, Typography} from "@mui/material";
import {FC, useEffect, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {WYSIWYG} from "@buildwitt/shapeshift-common/v1/wysiwyg";
import {Link} from "react-router-dom";
import {getAllFontStyles} from "@/templates/defaultTheme";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {palette} from "@/constants";
import {useCourseLibraryBanner} from "./hooks";

interface IStyleProps {
	loaded?: boolean;
	backgroundUrl?: string;
	desktopContentWidthPx?: number;
	bannerImageMinimumHeightPx?: number;
	mobileBannerImageMinimumHeightPx?: number;
}

const useStyles = makeStyles<IStyleProps>()((theme, {loaded, backgroundUrl, desktopContentWidthPx, bannerImageMinimumHeightPx, mobileBannerImageMinimumHeightPx}) => ({
	courseLibraryBanner: {
		display: "flex",
		flexDirection: "column",
		color: theme.palette.common.white,
		height: bannerImageMinimumHeightPx ?? "420px",
		background: backgroundUrl ? `url('${backgroundUrl}') no-repeat` : palette.black,
		backgroundSize: "cover",
		backgroundPosition: "center center",
		[theme.breakpoints.down("sm")]: {
			height: (mobileBannerImageMinimumHeightPx && loaded) ? `${mobileBannerImageMinimumHeightPx}px` : "150px"
		}
	},
	contentContainer: {
		maxWidth: desktopContentWidthPx ?? 1200,
		width: "100%",
		textAlign: "center",
		margin: "auto",
		[theme.breakpoints.down("sm")]: {
			padding: "0px"
		},
		[theme.breakpoints.up("sm")]: {
			padding: "50px"
		}
	},
	title: {
		fontWeight: 400,
		fontSize: "34px",
		[theme.breakpoints.down("sm")]: {
			fontSize: "32px"
		}
	},
	bannerContent: {
		...getAllFontStyles(theme)
	},
	btn: {
		marginTop: "20px"
	}
}));

export const CourseLibraryBanner: FC = () => {
	const {t} = useSSTranslation();
	const {data: apiData} = useCourseLibraryBanner();
	const bannerData = apiData?.data?.attributes;
	const [backgroundUrl, setImageUrl] = useState<string>();
	const [loaded, setLoaded] = useState(false);

	const {classes} = useStyles({
		backgroundUrl,
		desktopContentWidthPx: bannerData?.desktopContentWidthPx,
		bannerImageMinimumHeightPx: bannerData?.bannerImageMinimumHeightPx,
		mobileBannerImageMinimumHeightPx: bannerData?.mobileBannerImageMinimumHeightPx,
		loaded
	});

	useEffect(() => {
		const image = new Image();
		const url = bannerData?.backgroundImage?.data?.attributes?.formats?.large?.url ?? bannerData?.backgroundImage?.data?.attributes?.url;
		if (url) {
			image.src = url;
		} else {
			setLoaded(true);
		}

		image.onload = () => {
			if (image.src) {
				setImageUrl(image.src);
				setLoaded(true);
			}
		};
	}, [bannerData]);

	return (
		<Box className={classes.courseLibraryBanner}>
			<Box className={classes.contentContainer}>
				<Typography className={classes.title}>
					{bannerData?.title === "Course Library" ? t("nav_library", "Course Library") : bannerData?.title ?? t("nav_library", "Course Library")}
				</Typography>
				<WYSIWYG className={classes.bannerContent}>
					{bannerData?.content ?? ""}
				</WYSIWYG>
				{bannerData?.link && (
					<Button
						component={Link}
						to={bannerData.link}
						className={classes.btn}
					>
						Read More
					</Button>
				)}
			</Box>
		</Box>
	);
};

